interface GiteaRepo {
  id: number;
  name: string;
  full_name: string;
  description: string;
  html_url: string;
  stars_count: number;
  forks_count: number;
  updated_at: string;
  default_branch?: string | null;
  owner: { login: string };
}

interface GiteaCommit {
  sha: string;
  commit: { message: string; author: { name: string; date: string } };
  html_url: string;
}

interface GiteaBranch {
  name: string;
  commit?: { id?: string; sha?: string };
}

interface GiteaContent {
  name: string;
  path: string;
  type: 'file' | 'dir' | 'symlink' | 'submodule';
  size: number;
  html_url: string;
}

interface GiteaReadme {
  content: string;
  encoding: string;
  name: string;
  path: string;
}

let giteaRepoCache: GiteaRepo[] | null = null;
const giteaBranchCache = new Map<string, GiteaBranch[] | null>();
const giteaCommitCache = new Map<string, GiteaCommit[]>();
const giteaContentCache = new Map<string, GiteaContent[] | null>();
const giteaReadmeCache = new Map<string, GiteaReadme | null>();

export function isGiteaBrowserPath(): boolean {
  return window.location.pathname.endsWith('/gitea') || window.location.pathname.includes('/gitea/');
}

export function renderGiteaBrowser(): void {
  const page = shell();
  page.innerHTML = renderLoadingShell();
  void loadGiteaBrowser(page);
}

function shell(): HTMLElement {
  document.title = 'Gitea Repository Browser';
  injectStyles();

  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';

  const hide = (id: string): void => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  };
  hide('sidebar-scrim');
  hide('modal-overlay');
  hide('toast');

  const page = document.createElement('div');
  page.className = 'gb-page';
  root.appendChild(page);
  return page;
}

async function loadGiteaBrowser(page: HTMLElement): Promise<void> {
  try {
    const repos = await fetchAllGiteaRepos();
    page.innerHTML = renderBrowser(repos, repos[0] ?? null);
    wireBrowser(page, repos);
    if (repos[0]) void loadRepoDetail(page, repos[0]);
  } catch {
    page.innerHTML = renderUnavailable();
  }
}

function renderLoadingShell(): string {
  return `
    <div class="gb-shell">
      <header class="gb-header">
        <div>
          <div class="gb-brand">the hub ecosystems</div>
          <h1>Gitea Repository Browser</h1>
          <p>Read-only. No credentials exposed. No write actions.</p>
        </div>
        <div class="gb-count">Loading repositories...</div>
      </header>
      <div class="gb-loading">Loading repository list...</div>
    </div>
  `;
}

function renderBrowser(repos: GiteaRepo[], selected: GiteaRepo | null): string {
  return `
    <div class="gb-shell">
      <header class="gb-header">
        <div>
          <div class="gb-brand">the hub ecosystems</div>
          <h1>Gitea Repository Browser</h1>
          <p>Read-only. No credentials exposed. No write actions.</p>
        </div>
        <div class="gb-count">${repos.length} repositories</div>
      </header>
      <div class="gb-search-row">
        <input id="gb-filter" class="gb-search" type="search" placeholder="Filter repositories..." aria-label="Filter repositories">
      </div>
      <main class="gb-main">
        <aside class="gb-sidebar" aria-label="Repositories">
          <div class="gb-empty" id="gb-filter-empty" hidden>No repositories match the current filter.</div>
          <div class="gb-repo-list">
            ${repos.map((repo) => renderRepoListItem(repo, selected?.full_name === repo.full_name)).join('')}
          </div>
        </aside>
        <section class="gb-detail" id="gb-detail" aria-live="polite">
          ${selected ? renderRepoDetailLoading(selected) : '<div class="gb-placeholder">Select a repository from the list.</div>'}
        </section>
      </main>
      <footer class="gb-footer">
        <span>Read-only access through Hub Core proxy. No write operations. No credentials in frontend.</span>
        <span>Data sourced from internal Gitea instance. No external API calls.</span>
      </footer>
    </div>
  `;
}

function renderRepoListItem(repo: GiteaRepo, active: boolean): string {
  return `
    <button class="gb-repo-item${active ? ' gb-repo-item-active' : ''}" type="button" data-repo="${escAttr(repo.full_name)}" data-filter="${escAttr(`${repo.full_name} ${repo.description || ''}`.toLowerCase())}">
      <span class="gb-repo-name">${esc(repo.full_name)}</span>
      <span class="gb-repo-description">${esc(truncateText(repo.description || 'No description provided.', 96))}</span>
    </button>
  `;
}

function renderUnavailable(): string {
  return `
    <div class="gb-shell">
      <header class="gb-header">
        <div>
          <div class="gb-brand">the hub ecosystems</div>
          <h1>Gitea Repository Browser</h1>
          <p>Read-only. No credentials exposed. No write actions.</p>
        </div>
        <a class="gb-button" href="/repo/" target="_blank" rel="noopener">Open in Gitea</a>
      </header>
      <div class="gb-loading">Gitea API not available for anonymous access.</div>
      <footer class="gb-footer">
        <span>Read-only access through Hub Core proxy. No write operations. No credentials in frontend.</span>
        <span>Data sourced from internal Gitea instance. No external API calls.</span>
      </footer>
    </div>
  `;
}

function wireBrowser(page: HTMLElement, repos: GiteaRepo[]): void {
  const byFullName = new Map(repos.map((repo) => [repo.full_name, repo]));
  const filter = page.querySelector<HTMLInputElement>('#gb-filter');
  const empty = page.querySelector<HTMLElement>('#gb-filter-empty');

  page.querySelectorAll<HTMLButtonElement>('[data-repo]').forEach((button) => {
    button.addEventListener('click', () => {
      const fullName = button.dataset['repo'];
      const repo = fullName ? byFullName.get(fullName) : undefined;
      if (!repo) return;
      page.querySelectorAll<HTMLElement>('.gb-repo-item-active').forEach((el) => el.classList.remove('gb-repo-item-active'));
      button.classList.add('gb-repo-item-active');
      void loadRepoDetail(page, repo);
    });
  });

  filter?.addEventListener('input', () => {
    const query = filter.value.trim().toLowerCase();
    let visibleCount = 0;
    page.querySelectorAll<HTMLElement>('.gb-repo-item').forEach((item) => {
      const haystack = item.dataset['filter'] || '';
      const isVisible = !query || haystack.includes(query);
      item.hidden = !isVisible;
      if (isVisible) visibleCount += 1;
    });
    if (empty) empty.hidden = visibleCount !== 0;
  });
}

async function loadRepoDetail(page: HTMLElement, repo: GiteaRepo): Promise<void> {
  const detail = page.querySelector<HTMLElement>('#gb-detail');
  if (!detail) return;
  detail.innerHTML = renderRepoDetailLoading(repo);

  const [branches, commits, contents] = await Promise.all([
    fetchGiteaBranches(repo),
    fetchGiteaCommits(repo),
    fetchGiteaContents(repo, ''),
  ]);
  const readme = contents?.some((entry) => entry.type === 'file' && entry.name.toLowerCase() === 'readme.md')
    ? await fetchGiteaReadme(repo)
    : null;

  detail.innerHTML = renderRepoDetail(repo, branches, commits, contents, readme);
  wireFileBrowser(detail, repo);
}

function renderRepoDetailLoading(repo: GiteaRepo): string {
  return `
    <article class="gb-detail-inner">
      ${renderInfoBar(repo)}
      <section class="gb-section"><h2>Files</h2><div class="gb-loading">Loading repository detail...</div></section>
      <section class="gb-section"><h2>README</h2><div class="gb-loading">Loading README...</div></section>
      <section class="gb-section"><h2>Branches</h2><div class="gb-loading">Loading branches...</div></section>
      <section class="gb-section"><h2>Recent Commits</h2><div class="gb-loading">Loading commits...</div></section>
    </article>
  `;
}

function renderRepoDetail(
  repo: GiteaRepo,
  branches: GiteaBranch[] | null,
  commits: GiteaCommit[],
  contents: GiteaContent[] | null,
  readme: GiteaReadme | null,
): string {
  return `
    <article class="gb-detail-inner">
      ${renderInfoBar(repo)}
      <section class="gb-section">
        <h2>Files</h2>
        <div class="gb-files" data-file-browser data-path="">${renderFileBrowser(repo, contents, '')}</div>
      </section>
      <section class="gb-section">
        <h2>README</h2>
        ${renderReadme(readme, contents)}
      </section>
      <section class="gb-section">
        <h2>Branches</h2>
        ${renderBranches(branches)}
      </section>
      <section class="gb-section">
        <h2>Recent Commits</h2>
        ${renderCommits(commits, repo)}
      </section>
    </article>
  `;
}

function renderInfoBar(repo: GiteaRepo): string {
  return `
    <div class="gb-info">
      <div class="gb-info-main">
        <h2>${esc(repo.full_name)}</h2>
        <p>${esc(repo.description || 'No description provided.')}</p>
        <div class="gb-meta">
          <span class="gb-badge">${esc(repo.default_branch || 'unknown')}</span>
          <span>${repo.stars_count} stars</span>
          <span>${repo.forks_count} forks</span>
          <span>Updated ${esc(fmtDate(repo.updated_at))}</span>
        </div>
      </div>
      <a class="gb-button" href="${escAttr(giteaRepoHref(repo))}" target="_blank" rel="noopener">Open in Gitea</a>
    </div>
  `;
}

function renderFileBrowser(repo: GiteaRepo, contents: GiteaContent[] | null, path: string): string {
  if (contents === null) {
    return '<div class="gb-empty">File listing not available.</div>';
  }

  const sorted = [...contents].sort((a, b) => {
    if (a.type === 'dir' && b.type !== 'dir') return -1;
    if (a.type !== 'dir' && b.type === 'dir') return 1;
    return a.name.localeCompare(b.name);
  });
  const previousPath = path.split('/').filter(Boolean).slice(0, -1).join('/');
  const rows = [
    path ? renderBackRow(previousPath) : '',
    ...sorted.map((entry) => renderFileRow(repo, entry)),
  ].join('');

  return `
    ${renderBreadcrumb(path)}
    <div class="gb-file-table" role="table" aria-label="Repository files">
      <div class="gb-file-row gb-file-head" role="row">
        <span>Type</span>
        <span>Name</span>
        <span>Size</span>
      </div>
      ${rows || '<div class="gb-empty">No files found in this directory.</div>'}
    </div>
  `;
}

function renderBreadcrumb(path: string): string {
  const parts = path.split('/').filter(Boolean);
  let cursor = '';
  const crumbs = ['<button type="button" data-dir="">root</button>'];
  for (const part of parts) {
    cursor = cursor ? `${cursor}/${part}` : part;
    crumbs.push(`<button type="button" data-dir="${escAttr(cursor)}">${esc(part)}</button>`);
  }
  return `<div class="gb-breadcrumb" aria-label="Current path">${crumbs.join('<span>/</span>')}</div>`;
}

function renderBackRow(previousPath: string): string {
  return `
    <button class="gb-file-row gb-dir-row" type="button" role="row" data-dir="${escAttr(previousPath)}">
      <span>[dir]</span>
      <span>..</span>
      <span>back</span>
    </button>
  `;
}

function renderFileRow(repo: GiteaRepo, entry: GiteaContent): string {
  if (entry.type === 'dir') {
    return `
      <button class="gb-file-row gb-dir-row" type="button" role="row" data-dir="${escAttr(entry.path)}">
        <span>[dir]</span>
        <span>${esc(entry.name)}</span>
        <span>directory</span>
      </button>
    `;
  }

  return `
    <a class="gb-file-row" role="row" href="${escAttr(giteaFileHref(repo, entry.path))}" target="_blank" rel="noopener">
      <span>[file]</span>
      <span>${esc(entry.name)}</span>
      <span>${esc(formatBytes(entry.size))}</span>
    </a>
  `;
}

function wireFileBrowser(detail: HTMLElement, repo: GiteaRepo): void {
  const browser = detail.querySelector<HTMLElement>('[data-file-browser]');
  if (!browser) return;

  browser.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-dir]');
    if (!button) return;
    const path = button.dataset['dir'] || '';
    browser.dataset['path'] = path;
    browser.innerHTML = '<div class="gb-loading">Loading files...</div>';
    void fetchGiteaContents(repo, path).then((contents) => {
      browser.innerHTML = renderFileBrowser(repo, contents, path);
    });
  });
}

function renderReadme(readme: GiteaReadme | null, rootContents: GiteaContent[] | null): string {
  const hasReadme = rootContents?.some((entry) => entry.type === 'file' && entry.name.toLowerCase() === 'readme.md') || false;
  if (!hasReadme) return '<div class="gb-empty">No README found.</div>';
  if (!readme) return '<div class="gb-empty">README not available.</div>';

  const decoded = decodeGiteaReadme(readme);
  const truncated = decoded.length > 3000 ? `${decoded.slice(0, 3000)}... truncated` : decoded;
  return `<pre class="gb-readme">${esc(truncated)}</pre>`;
}

function renderBranches(branches: GiteaBranch[] | null): string {
  if (branches === null) return '<div class="gb-empty">Branch info not available.</div>';
  if (branches.length === 0) return '<div class="gb-empty">Branch info not available.</div>';

  return `
    <div class="gb-branches">
      ${branches.map((branch) => {
        const sha = branch.commit?.sha || branch.commit?.id || '';
        return `
          <div class="gb-branch">
            <span>${esc(branch.name)}</span>
            ${sha ? `<code>${esc(sha.slice(0, 7))}</code>` : ''}
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function renderCommits(commits: GiteaCommit[], repo: GiteaRepo): string {
  if (commits.length === 0) return '<div class="gb-empty">Commits not available.</div>';

  return `
    <div class="gb-commits">
      ${commits.map((commit) => {
        const firstLine = commit.commit.message.split('\n')[0] || 'No commit message';
        return `
          <div class="gb-commit">
            <a href="${escAttr(giteaCommitHref(repo, commit))}" target="_blank" rel="noopener"><code>${esc(commit.sha.slice(0, 7))}</code></a>
            <span class="gb-commit-message">${esc(truncateText(firstLine, 120))}</span>
            <span class="gb-commit-meta">${esc(commit.commit.author.name)} - ${esc(fmtDate(commit.commit.author.date))}</span>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

async function fetchAllGiteaRepos(): Promise<GiteaRepo[]> {
  if (giteaRepoCache) return giteaRepoCache;

  const repos: GiteaRepo[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const res = await fetch(`/repo/api/v1/repos/search?limit=50&sort=updated&order=desc&page=${page}`);
    if (!res.ok) throw new Error('not ok');
    const json: unknown = await res.json();
    if (!isRecord(json) || !Array.isArray(json['data'])) throw new Error('not ok');
    const pageItems = json['data'];
    const pageRepos = pageItems.filter(isGiteaRepo);
    repos.push(...pageRepos);
    hasMore = pageItems.length === 50;
    page += 1;
  }

  giteaRepoCache = repos;
  return repos;
}

async function fetchGiteaBranches(repo: GiteaRepo): Promise<GiteaBranch[] | null> {
  const key = giteaRepoKey(repo);
  if (giteaBranchCache.has(key)) return giteaBranchCache.get(key) || null;
  try {
    const res = await fetch(`/repo/api/v1/repos/${encodeURIComponent(repo.owner.login)}/${encodeURIComponent(repo.name)}/branches`);
    if (!res.ok) return null;
    const json: unknown = await res.json();
    const branches = Array.isArray(json) ? json.filter(isGiteaBranch) : null;
    giteaBranchCache.set(key, branches);
    return branches;
  } catch {
    giteaBranchCache.set(key, null);
    return null;
  }
}

async function fetchGiteaCommits(repo: GiteaRepo): Promise<GiteaCommit[]> {
  const key = giteaRepoKey(repo);
  const cached = giteaCommitCache.get(key);
  if (cached) return cached;
  try {
    const res = await fetch(`/repo/api/v1/repos/${encodeURIComponent(repo.owner.login)}/${encodeURIComponent(repo.name)}/commits?limit=10`);
    if (!res.ok) return [];
    const json: unknown = await res.json();
    const commits = Array.isArray(json) ? json.filter(isGiteaCommit) : [];
    giteaCommitCache.set(key, commits);
    return commits;
  } catch {
    giteaCommitCache.set(key, []);
    return [];
  }
}

async function fetchGiteaContents(repo: GiteaRepo, path: string): Promise<GiteaContent[] | null> {
  const key = giteaContentCacheKey(repo, path);
  if (giteaContentCache.has(key)) return giteaContentCache.get(key) || null;
  const encodedPath = encodeGiteaPath(path);
  const suffix = encodedPath ? `/${encodedPath}` : '/';

  try {
    const res = await fetch(`/repo/api/v1/repos/${encodeURIComponent(repo.owner.login)}/${encodeURIComponent(repo.name)}/contents${suffix}`);
    if (!res.ok) {
      giteaContentCache.set(key, null);
      return null;
    }
    const json: unknown = await res.json();
    const contents = Array.isArray(json) ? json.filter(isGiteaContent) : null;
    giteaContentCache.set(key, contents);
    return contents;
  } catch {
    giteaContentCache.set(key, null);
    return null;
  }
}

async function fetchGiteaReadme(repo: GiteaRepo): Promise<GiteaReadme | null> {
  const key = giteaRepoKey(repo);
  if (giteaReadmeCache.has(key)) return giteaReadmeCache.get(key) || null;

  try {
    const res = await fetch(`/repo/api/v1/repos/${encodeURIComponent(repo.owner.login)}/${encodeURIComponent(repo.name)}/readme`);
    if (!res.ok) {
      giteaReadmeCache.set(key, null);
      return null;
    }
    const json: unknown = await res.json();
    const readme = isGiteaReadme(json) ? json : null;
    giteaReadmeCache.set(key, readme);
    return readme;
  } catch {
    giteaReadmeCache.set(key, null);
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isGiteaRepo(value: unknown): value is GiteaRepo {
  if (!isRecord(value) || !isRecord(value['owner'])) return false;
  return typeof value['id'] === 'number'
    && typeof value['name'] === 'string'
    && typeof value['full_name'] === 'string'
    && typeof value['html_url'] === 'string'
    && typeof value['stars_count'] === 'number'
    && typeof value['forks_count'] === 'number'
    && typeof value['updated_at'] === 'string'
    && typeof value['owner']['login'] === 'string'
    && (typeof value['default_branch'] === 'string' || value['default_branch'] === null || value['default_branch'] === undefined)
    && (typeof value['description'] === 'string' || value['description'] === null || value['description'] === undefined);
}

function isGiteaCommit(value: unknown): value is GiteaCommit {
  if (!isRecord(value) || !isRecord(value['commit'])) return false;
  const commit = value['commit'];
  const author = commit['author'];
  return typeof value['sha'] === 'string'
    && typeof value['html_url'] === 'string'
    && typeof commit['message'] === 'string'
    && isRecord(author)
    && typeof author['name'] === 'string'
    && typeof author['date'] === 'string';
}

function isGiteaBranch(value: unknown): value is GiteaBranch {
  return isRecord(value) && typeof value['name'] === 'string';
}

function isGiteaContent(value: unknown): value is GiteaContent {
  if (!isRecord(value)) return false;
  const type = value['type'];
  return typeof value['name'] === 'string'
    && typeof value['path'] === 'string'
    && (type === 'file' || type === 'dir' || type === 'symlink' || type === 'submodule')
    && typeof value['size'] === 'number'
    && typeof value['html_url'] === 'string';
}

function isGiteaReadme(value: unknown): value is GiteaReadme {
  return isRecord(value)
    && typeof value['content'] === 'string'
    && typeof value['encoding'] === 'string'
    && typeof value['name'] === 'string'
    && typeof value['path'] === 'string';
}

function giteaRepoHref(repo: GiteaRepo): string {
  return `/repo/${encodeURIComponent(repo.owner.login)}/${encodeURIComponent(repo.name)}`;
}

function giteaCommitHref(repo: GiteaRepo, commit: GiteaCommit): string {
  return `${giteaRepoHref(repo)}/commit/${encodeURIComponent(commit.sha)}`;
}

function giteaRepoKey(repo: GiteaRepo): string {
  return `${repo.owner.login}/${repo.name}`;
}

function encodeGiteaPath(path: string): string {
  return path.split('/').filter(Boolean).map(encodeURIComponent).join('/');
}

function giteaFileHref(repo: GiteaRepo, path: string): string {
  const branch = repo.default_branch || 'master';
  return `${giteaRepoHref(repo)}/src/branch/${encodeURIComponent(branch)}/${encodeGiteaPath(path)}`;
}

function giteaContentCacheKey(repo: GiteaRepo, path: string): string {
  return `${giteaRepoKey(repo)}:${path}`;
}

function decodeGiteaReadme(readme: GiteaReadme): string {
  if (readme.encoding.toLowerCase() !== 'base64') return readme.content;
  try {
    const normalized = readme.content.replace(/\s/g, '');
    const binary = atob(normalized);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new TextDecoder().decode(bytes);
  } catch {
    return 'README content could not be decoded.';
  }
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function truncateText(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function escAttr(s: string): string {
  return esc(s).replace(/"/g, '&quot;');
}

function injectStyles(): void {
  if (document.getElementById('gitea-browser-styles')) return;
  const style = document.createElement('style');
  style.id = 'gitea-browser-styles';
  style.textContent = `
body.sp-public-body {
  display: block !important;
  min-height: 100dvh;
  height: auto !important;
  overflow-y: auto !important;
  background: var(--bg) !important;
}
.sp-public-root {
  display: block !important;
  width: 100% !important;
  min-height: 100dvh !important;
  height: auto !important;
  overflow: visible !important;
  flex: none !important;
  background: var(--bg);
}
.gb-page {
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: var(--bg);
  color: var(--fg);
  padding: 24px;
}
.gb-shell {
  width: 100%;
  max-width: 1380px;
  margin: 0 auto;
}
.gb-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}
.gb-brand {
  color: var(--fg-muted);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.gb-header h1 {
  margin: 6px 0;
  color: var(--fg);
  font-size: 26px;
  line-height: 1.2;
}
.gb-header p,
.gb-info p {
  margin: 0;
  color: var(--fg-muted);
  font-size: 14px;
  line-height: 1.45;
}
.gb-count,
.gb-badge,
.gb-button {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 3px 10px;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--fg);
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.gb-button {
  border-color: var(--accent);
  color: var(--accent);
  white-space: nowrap;
}
.gb-search-row {
  margin: 16px 0;
}
.gb-search {
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  font-size: 14px;
}
.gb-main {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}
.gb-sidebar,
.gb-detail {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg);
}
.gb-sidebar {
  max-height: calc(100dvh - 190px);
  overflow: auto;
}
.gb-repo-list {
  display: flex;
  flex-direction: column;
}
.gb-repo-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 11px 12px;
  border: 0;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
  color: var(--fg);
  text-align: left;
  cursor: pointer;
}
.gb-repo-item:hover,
.gb-repo-item-active {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}
.gb-repo-name {
  font-size: 13px;
  font-weight: 800;
  overflow-wrap: anywhere;
}
.gb-repo-description {
  color: var(--fg-muted);
  font-size: 12px;
  line-height: 1.35;
}
.gb-detail {
  min-height: calc(100dvh - 190px);
  overflow: hidden;
}
.gb-detail-inner {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
}
.gb-placeholder,
.gb-loading,
.gb-empty {
  padding: 16px;
  color: var(--fg-muted);
  font-size: 13px;
}
.gb-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
}
.gb-info-main {
  min-width: 0;
}
.gb-info h2,
.gb-section h2 {
  margin: 0 0 8px;
  color: var(--fg);
  font-size: 18px;
  line-height: 1.25;
  overflow-wrap: anywhere;
}
.gb-section h2 {
  font-size: 15px;
}
.gb-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  color: var(--fg-muted);
  font-size: 12px;
}
.gb-section {
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
}
.gb-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  color: var(--fg-muted);
  font-size: 12px;
}
.gb-breadcrumb button {
  padding: 2px 6px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--bg);
  color: var(--accent);
  cursor: pointer;
}
.gb-file-table {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.gb-file-row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 96px;
  gap: 10px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 7px 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  text-align: left;
  text-decoration: none;
  font-family: monospace;
  font-size: 12px;
}
.gb-file-head {
  color: var(--fg-muted);
  font-weight: 700;
}
.gb-dir-row {
  cursor: pointer;
}
.gb-dir-row:hover,
.gb-file-row:hover {
  border-color: var(--accent);
}
.gb-file-row span:nth-child(2) {
  overflow-wrap: anywhere;
}
.gb-file-row span:nth-child(3) {
  color: var(--fg-muted);
  text-align: right;
}
.gb-readme {
  max-height: 420px;
  overflow: auto;
  margin: 0;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.45;
}
.gb-branches,
.gb-commits {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.gb-branch,
.gb-commit {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--fg);
  font-size: 13px;
}
.gb-commit {
  grid-template-columns: 72px minmax(0, 1fr) auto;
}
.gb-branch code,
.gb-commit code {
  color: var(--accent);
  font-family: monospace;
}
.gb-commit-message {
  overflow-wrap: anywhere;
}
.gb-commit-meta {
  color: var(--fg-muted);
  font-size: 12px;
  text-align: right;
}
.gb-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--border);
  color: var(--fg-muted);
  font-size: 12px;
}
@media (max-width: 768px) {
  .gb-page {
    padding: 16px;
  }
  .gb-header,
  .gb-info {
    flex-direction: column;
  }
  .gb-main {
    grid-template-columns: 1fr;
  }
  .gb-sidebar {
    max-height: 320px;
  }
  .gb-detail {
    min-height: 0;
  }
  .gb-file-row {
    grid-template-columns: 56px minmax(0, 1fr) 74px;
  }
  .gb-commit {
    grid-template-columns: 1fr;
  }
  .gb-commit-meta {
    text-align: left;
  }
}
`;
  document.head.appendChild(style);
}
